import { useQuery } from '@tanstack/react-query';
import { httpClient } from '@/utils/httpClient';
import { API_CONFIG } from '@/config/api';
import { useAuthStore } from '@/stores/useAuthStore';

/**
 * Returns the person entity ID that is linked to the current session user,
 * or `null` if:
 *   - the user is not authenticated
 *   - the user has no linked person record (service accounts, etc.)
 *   - the RPC call is still loading
 *
 * Used to determine "own record" visibility rules in the sizing workflow
 * (issue #245): service members see the Sizing action only on their own row
 * and the PersonSizingDialog shows a read-only view for other records.
 *
 * The underlying DB function (api_v1.my_person_entity_id — migration 362)
 * is STABLE and SECURITY DEFINER; the result is safe to cache for the
 * lifetime of the session.
 */
export const useMyPersonEntityId = (): {
  myPersonEntityId: string | null;
  isLoading: boolean;
} => {
  // Use the store directly (not useAuth) to avoid a circular import:
  // useAuth → useMyPersonEntityId → useAuth.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const { data, isLoading } = useQuery<string | null>({
    queryKey: ['my-person-entity-id'],
    queryFn: async () => {
      const result = await httpClient.get<string | null>(
        `${API_CONFIG.ENDPOINTS.RPC}/my_person_entity_id`,
      );
      return result ?? null;
    },
    enabled: isAuthenticated,
    // Cache for the session — person linkage does not change without a re-login
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return {
    myPersonEntityId: data ?? null,
    isLoading: isAuthenticated ? isLoading : false,
  };
};
